import { useState, useRef, useEffect } from 'react';
import { communicationsAPI } from '../lib/api';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import {
  PhoneIncoming, Clock, Users, Voicemail, PhoneOff,
  MessageSquare, Menu, PhoneForwarded, Music, Volume2,
  Play, RotateCcw, X, Hash, Timer, AlertCircle,
  CheckCircle2, ArrowRight, Phone, Pause,
} from 'lucide-react';

// ── Step metadata — reuse the same palette as FlowPreview ──
const SM = {
  incoming:   { label: 'Incoming Call',  icon: PhoneIncoming,  bg: 'bg-blue-50',   border: 'border-blue-200',   text: 'text-blue-700',   iconBg: 'bg-blue-100' },
  greeting:   { label: 'Greeting',       icon: MessageSquare,  bg: 'bg-teal-50',   border: 'border-teal-200',   text: 'text-teal-700',   iconBg: 'bg-teal-100' },
  call_hours: { label: 'Call Hours',     icon: Clock,          bg: 'bg-amber-50',  border: 'border-amber-200',  text: 'text-amber-700',  iconBg: 'bg-amber-100' },
  menu:       { label: 'IVR Menu',       icon: Menu,           bg: 'bg-indigo-50', border: 'border-indigo-200', text: 'text-indigo-700', iconBg: 'bg-indigo-100' },
  simul_ring: { label: 'Simul-Ring',     icon: Users,          bg: 'bg-green-50',  border: 'border-green-200',  text: 'text-green-700',  iconBg: 'bg-green-100' },
  forward:    { label: 'Forward Call',   icon: PhoneForwarded, bg: 'bg-cyan-50',   border: 'border-cyan-200',   text: 'text-cyan-700',   iconBg: 'bg-cyan-100' },
  voicemail:  { label: 'Voicemail',      icon: Voicemail,      bg: 'bg-purple-50', border: 'border-purple-200', text: 'text-purple-700', iconBg: 'bg-purple-100' },
  end_call:   { label: 'End Call',       icon: PhoneOff,       bg: 'bg-red-50',    border: 'border-red-200',    text: 'text-red-700',    iconBg: 'bg-red-100' },
};
function meta(t) { return SM[t] || SM.end_call; }

// ── Simulation Engine ──
// Walks steps_json and produces log entries + waits for user input at decision points

const STATUS = { RUNNING: 'running', WAITING: 'waiting', ENDED: 'ended' };

function createSimState(steps, hoursOverride) {
  return {
    steps,
    hoursOverride, // 'open' | 'closed' | null (null = use 24/7 default)
    log: [{ type: 'system', icon: 'phone', text: 'Incoming call received.', ts: Date.now() }],
    status: STATUS.RUNNING,
    pointer: null, // { steps, index } — current position in the tree
    waitingFor: null, // null | 'menu' | 'call_hours'
    currentStep: null,
    menuDigits: [], // available digits for current menu
    terminal: null, // 'voicemail' | 'end_call' | 'forward' | 'ring'
  };
}

function addLog(state, entry) {
  return { ...state, log: [...state.log, { ...entry, ts: Date.now() }] };
}

// Advance the simulation by one step. Returns new state.
// If the step needs user input (menu, call_hours), sets waitingFor and pauses.
function advanceSim(state) {
  let s = { ...state };

  // Initialize pointer if not set
  if (!s.pointer) {
    s.pointer = { steps: s.steps, index: 0 };
  }

  const { steps: currentSteps, index } = s.pointer;

  // Check if we've run past the end
  if (index >= currentSteps.length) {
    s = addLog(s, { type: 'system', icon: 'end', text: 'Call ended (no more steps).' });
    s.status = STATUS.ENDED;
    s.terminal = 'end_call';
    return s;
  }

  const step = currentSteps[index];
  const config = step.config || {};
  const branches = step.branches || {};
  s.currentStep = step;

  switch (step.type) {
    case 'incoming':
      s = addLog(s, { type: 'step', stepType: 'incoming', text: 'Call received by the system.' });
      s.pointer = { steps: currentSteps, index: index + 1 };
      return advanceSim(s); // auto-advance

    case 'greeting': {
      const promptType = config.audio_file_id ? 'Audio File' : 'TTS';
      const promptText = config.message || config.prompt_text || '';
      s = addLog(s, {
        type: 'step', stepType: 'greeting',
        text: `Greeting played (${promptType}).`,
        detail: promptType === 'TTS' && promptText ? `"${promptText}"` : undefined,
        audioId: config.audio_file_id || undefined,
      });
      s.pointer = { steps: currentSteps, index: index + 1 };
      return advanceSim(s); // auto-advance
    }

    case 'call_hours': {
      const mode = config.mode || '24_7';
      if (mode === '24_7') {
        s = addLog(s, { type: 'step', stepType: 'call_hours', text: 'Call Hours evaluated: 24/7 — Always open.' });
        // If branches exist, take open path
        if (branches.open) {
          s = addLog(s, { type: 'branch', text: 'Branch taken: Open Hours.' });
          s.pointer = { steps: branches.open, index: 0 };
          return advanceSim(s);
        }
        s.pointer = { steps: currentSteps, index: index + 1 };
        return advanceSim(s);
      }
      // Non-24/7: need user to choose open vs closed
      if (branches.open || branches.closed) {
        const modeLabel = mode === 'business_hours' ? 'Business Hours' : 'Custom Hours';
        s = addLog(s, { type: 'step', stepType: 'call_hours', text: `Call Hours evaluated: ${modeLabel}.` });
        // If we have an override from the user's initial choice, use it
        if (s.hoursOverride) {
          const isOpen = s.hoursOverride === 'open';
          s = addLog(s, { type: 'branch', text: `Branch taken: ${isOpen ? 'Open Hours' : 'After Hours'} (simulated).` });
          const branchSteps = isOpen ? (branches.open || []) : (branches.closed || []);
          if (branchSteps.length > 0) {
            s.pointer = { steps: branchSteps, index: 0 };
          } else {
            s.pointer = { steps: currentSteps, index: index + 1 };
          }
          return advanceSim(s);
        }
        // No override: pause and ask user
        s.waitingFor = 'call_hours';
        s.status = STATUS.WAITING;
        return s;
      }
      // No branches (legacy linear): just advance
      s = addLog(s, { type: 'step', stepType: 'call_hours', text: `Call Hours: ${mode === 'business_hours' ? 'Business Hours' : 'Custom Hours'} (legacy linear).` });
      s.pointer = { steps: currentSteps, index: index + 1 };
      return advanceSim(s);
    }

    case 'menu': {
      const promptType = config.audio_file_id ? 'Audio File' : 'TTS';
      const promptText = config.prompt_text || '';
      s = addLog(s, {
        type: 'step', stepType: 'menu',
        text: `IVR Menu prompt played (${promptType}).`,
        detail: promptType === 'TTS' && promptText ? `"${promptText}"` : undefined,
        audioId: config.audio_file_id || undefined,
      });
      const digitKeys = Object.keys(branches).filter(k => /^[0-9*#]$/.test(k));
      const hasTimeout = 'timeout' in branches;
      const hasInvalid = 'invalid' in branches;
      s.menuDigits = digitKeys.sort((a, b) => '1234567890*#'.indexOf(a) - '1234567890*#'.indexOf(b));
      s.menuHasTimeout = hasTimeout;
      s.menuHasInvalid = hasInvalid;
      s.waitingFor = 'menu';
      s.status = STATUS.WAITING;
      return s;
    }

    case 'simul_ring': {
      const target = config.target || 'all_web_phones';
      const timeout = config.timeout || 25;
      let targetLabel = 'all web phones';
      if (target === 'ring_group') targetLabel = 'ring group';
      if (target === 'employee') targetLabel = 'specific employee';
      s = addLog(s, { type: 'step', stepType: 'simul_ring', text: `Ringing ${targetLabel} for ${timeout} seconds.` });
      s = addLog(s, { type: 'outcome', text: `Ring completed. Call continues to next step.` });
      s.pointer = { steps: currentSteps, index: index + 1 };
      // Check if this is the last meaningful step
      if (index + 1 >= currentSteps.length) {
        s = addLog(s, { type: 'system', icon: 'end', text: 'Call ended after ringing.' });
        s.status = STATUS.ENDED;
        s.terminal = 'ring';
        return s;
      }
      return advanceSim(s);
    }

    case 'forward': {
      const number = config.number || '(no number)';
      const timeout = config.timeout || 30;
      s = addLog(s, { type: 'step', stepType: 'forward', text: `Forwarding call to ${number} (${timeout}s timeout).` });
      s = addLog(s, { type: 'outcome', text: `Forward completed. Call continues.` });
      s.pointer = { steps: currentSteps, index: index + 1 };
      if (index + 1 >= currentSteps.length) {
        s = addLog(s, { type: 'system', icon: 'end', text: 'Call ended after forward.' });
        s.status = STATUS.ENDED;
        s.terminal = 'forward';
        return s;
      }
      return advanceSim(s);
    }

    case 'voicemail': {
      const promptType = config.audio_file_id ? 'Audio File' : 'TTS';
      const promptText = config.message || '';
      s = addLog(s, {
        type: 'step', stepType: 'voicemail',
        text: `Voicemail greeting played (${promptType}). Recording started.`,
        detail: promptType === 'TTS' && promptText ? `"${promptText}"` : undefined,
        audioId: config.audio_file_id || undefined,
      });
      s = addLog(s, { type: 'system', icon: 'end', text: 'Call ended — message recorded.' });
      s.status = STATUS.ENDED;
      s.terminal = 'voicemail';
      return s;
    }

    case 'end_call':
      s = addLog(s, { type: 'step', stepType: 'end_call', text: 'Call terminated (hangup).' });
      s.status = STATUS.ENDED;
      s.terminal = 'end_call';
      return s;

    default:
      s = addLog(s, { type: 'system', text: `Unknown step type: ${step.type}. Skipping.` });
      s.pointer = { steps: currentSteps, index: index + 1 };
      return advanceSim(s);
  }
}

// Handle user choosing Open or Closed for call_hours
function resolveHours(state, choice) {
  let s = { ...state, waitingFor: null, status: STATUS.RUNNING };
  const step = s.currentStep;
  const branches = step?.branches || {};
  const isOpen = choice === 'open';
  s = addLog(s, { type: 'branch', text: `Branch taken: ${isOpen ? 'Open Hours' : 'After Hours'} (user chose).` });
  const branchSteps = isOpen ? (branches.open || []) : (branches.closed || []);
  if (branchSteps.length > 0) {
    s.pointer = { steps: branchSteps, index: 0 };
  } else {
    // Empty branch: advance past call_hours in parent
    s = addLog(s, { type: 'system', text: `${isOpen ? 'Open' : 'Closed'} branch is empty.` });
    s.status = STATUS.ENDED;
    s.terminal = 'end_call';
    return s;
  }
  return advanceSim(s);
}

// Handle user pressing a digit, timeout, or invalid for menu
function resolveMenu(state, action) {
  let s = { ...state, waitingFor: null, status: STATUS.RUNNING };
  const step = s.currentStep;
  const branches = step?.branches || {};

  if (action === 'timeout') {
    s = addLog(s, { type: 'branch', text: 'No input received (timeout).' });
    const timeoutSteps = branches.timeout || [];
    if (timeoutSteps.length > 0) {
      s.pointer = { steps: timeoutSteps, index: 0 };
    } else {
      s = addLog(s, { type: 'system', text: 'No timeout branch defined. Call ended.' });
      s.status = STATUS.ENDED;
      s.terminal = 'end_call';
      return s;
    }
  } else if (action === 'invalid') {
    s = addLog(s, { type: 'branch', text: 'Invalid input received.' });
    const invalidSteps = branches.invalid || [];
    if (invalidSteps.length > 0) {
      s.pointer = { steps: invalidSteps, index: 0 };
    } else {
      s = addLog(s, { type: 'system', text: 'No invalid-input branch defined. Call ended.' });
      s.status = STATUS.ENDED;
      s.terminal = 'end_call';
      return s;
    }
  } else {
    // Digit press
    s = addLog(s, { type: 'branch', text: `Caller pressed ${action}.` });
    const digitSteps = branches[action] || [];
    if (digitSteps.length > 0) {
      s.pointer = { steps: digitSteps, index: 0 };
    } else {
      s = addLog(s, { type: 'system', text: `No branch for digit ${action}. Call ended.` });
      s.status = STATUS.ENDED;
      s.terminal = 'end_call';
      return s;
    }
  }
  return advanceSim(s);
}


// ══════════════════════════════════════════════════════════════
// Log Entry Component
// ══════════════════════════════════════════════════════════════

function logIcon(entry) {
  if (entry.stepType) {
    const m = meta(entry.stepType);
    return { Icon: m.icon, cls: `${m.iconBg} ${m.text}` };
  }
  if (entry.type === 'branch') return { Icon: ArrowRight, cls: 'bg-indigo-100 text-indigo-700' };
  if (entry.type === 'outcome') return { Icon: CheckCircle2, cls: 'bg-green-100 text-green-700' };
  return { Icon: Phone, cls: 'bg-slate-100 text-slate-600' };
}

function LogEntry({ entry, idx }) {
  const { Icon, cls } = logIcon(entry);
  const isStep = entry.type === 'step';
  const isBranch = entry.type === 'branch';
  const isEnd = entry.text?.includes('ended') || entry.text?.includes('terminated');

  return (
    <div className={`flex gap-2.5 ${idx > 0 ? 'mt-2' : ''}`} data-testid={`sim-log-${idx}`}>
      <div className={`w-6 h-6 rounded flex items-center justify-center shrink-0 ${cls}`}>
        <Icon className="w-3 h-3" />
      </div>
      <div className="flex-1 min-w-0">
        <p className={`text-xs leading-snug ${isEnd ? 'font-semibold' : isStep ? 'font-medium' : isBranch ? 'text-indigo-700' : 'text-muted-foreground'}`}>
          {entry.text}
        </p>
        {entry.detail && (
          <p className="text-[10px] text-muted-foreground mt-0.5 break-words">{entry.detail}</p>
        )}
        {entry.audioId && (
          <audio
            src={communicationsAPI.audioFileUrl(entry.audioId)}
            controls
            preload="none"
            className="mt-1 h-7 w-full max-w-[240px]"
            data-testid={`sim-audio-${idx}`}
          />
        )}
      </div>
    </div>
  );
}


// ══════════════════════════════════════════════════════════════
// FlowSimulator — main exported component
// ══════════════════════════════════════════════════════════════

export function FlowSimulator({ flow, onClose }) {
  const steps = flow?.steps_json || [];
  const [sim, setSim] = useState(null);
  const [hoursChoice, setHoursChoice] = useState(null); // pre-selected hours override
  const logEndRef = useRef(null);

  // Auto-scroll log
  useEffect(() => {
    logEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [sim?.log?.length]);

  const startSim = (hoursOverride) => {
    let state = createSimState(steps, hoursOverride || null);
    state = advanceSim(state);
    setSim(state);
    setHoursChoice(hoursOverride || null);
  };

  const handleMenuChoice = (action) => {
    if (!sim || sim.waitingFor !== 'menu') return;
    setSim(resolveMenu(sim, action));
  };

  const handleHoursChoice = (choice) => {
    if (!sim || sim.waitingFor !== 'call_hours') return;
    setSim(resolveHours(sim, choice));
  };

  const restart = () => {
    startSim(hoursChoice);
  };

  // Check if flow has non-24/7 call hours to show start options
  const hasNon247Hours = steps.some(s => s.type === 'call_hours' && s.config?.mode !== '24_7' && s.branches);

  // Not started yet: show start screen
  if (!sim) {
    return (
      <div className="space-y-4" data-testid="sim-start-screen">
        <div className="text-center py-6">
          <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center mx-auto mb-3">
            <Play className="w-5 h-5 text-blue-700" />
          </div>
          <h3 className="text-sm font-semibold mb-1">Simulate Call Flow</h3>
          <p className="text-xs text-muted-foreground max-w-xs mx-auto">
            Walk through &ldquo;{flow?.name}&rdquo; step-by-step to verify routing, menu choices, and terminal outcomes.
          </p>
        </div>

        {hasNon247Hours && (
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-center" data-testid="sim-hours-chooser">
            <p className="text-xs font-medium text-amber-800 mb-2">This flow uses Call Hours. Choose a scenario:</p>
            <div className="flex gap-2 justify-center">
              <Button size="sm" variant="outline" className="text-[11px] gap-1" onClick={() => startSim('open')} data-testid="sim-start-open">
                <Clock className="w-3 h-3" /> Open Hours
              </Button>
              <Button size="sm" variant="outline" className="text-[11px] gap-1" onClick={() => startSim('closed')} data-testid="sim-start-closed">
                <PhoneOff className="w-3 h-3" /> After Hours
              </Button>
            </div>
          </div>
        )}

        <div className="text-center">
          <Button onClick={() => startSim(null)} data-testid="sim-start-btn">
            <Play className="w-4 h-4 mr-1.5" /> Start Simulation
          </Button>
        </div>
      </div>
    );
  }

  // Simulation running / waiting / ended
  const isWaiting = sim.status === STATUS.WAITING;
  const isEnded = sim.status === STATUS.ENDED;

  return (
    <div className="flex flex-col h-full" data-testid="sim-active">
      {/* Header */}
      <div className="flex items-center justify-between pb-3 border-b shrink-0">
        <div className="flex items-center gap-2">
          <div className={`w-2.5 h-2.5 rounded-full ${isEnded ? 'bg-red-500' : isWaiting ? 'bg-amber-500 animate-pulse' : 'bg-green-500 animate-pulse'}`} />
          <span className="text-xs font-semibold">
            {isEnded ? 'Simulation Complete' : isWaiting ? 'Awaiting Input' : 'Simulating...'}
          </span>
          {hoursChoice && (
            <Badge variant="outline" className="text-[9px]">{hoursChoice === 'open' ? 'Open Hours' : 'After Hours'}</Badge>
          )}
        </div>
        <div className="flex items-center gap-1">
          <Button size="sm" variant="ghost" className="h-7 text-[11px] gap-1" onClick={restart} data-testid="sim-restart-btn">
            <RotateCcw className="w-3 h-3" /> Restart
          </Button>
        </div>
      </div>

      {/* Main area: log + controls */}
      <div className="flex-1 overflow-y-auto py-3 min-h-0" data-testid="sim-log">
        {sim.log.map((entry, idx) => (
          <LogEntry key={idx} entry={entry} idx={idx} />
        ))}
        <div ref={logEndRef} />
      </div>

      {/* Interactive controls */}
      {isWaiting && sim.waitingFor === 'menu' && (
        <div className="border-t pt-3 shrink-0 space-y-2" data-testid="sim-menu-controls">
          <p className="text-[11px] font-medium text-muted-foreground uppercase tracking-wide">Choose an action:</p>
          <div className="flex flex-wrap gap-1.5">
            {(sim.menuDigits || []).map(d => (
              <Button
                key={d}
                size="sm"
                variant="outline"
                className="h-8 min-w-[44px] text-xs font-semibold gap-1"
                onClick={() => handleMenuChoice(d)}
                data-testid={`sim-press-${d}`}
              >
                <Hash className="w-3 h-3" /> {d}
              </Button>
            ))}
            {sim.menuHasTimeout && (
              <Button
                size="sm"
                variant="outline"
                className="h-8 text-xs gap-1 text-amber-700 border-amber-300 hover:bg-amber-50"
                onClick={() => handleMenuChoice('timeout')}
                data-testid="sim-timeout"
              >
                <Timer className="w-3 h-3" /> Timeout
              </Button>
            )}
            {sim.menuHasInvalid && (
              <Button
                size="sm"
                variant="outline"
                className="h-8 text-xs gap-1 text-red-700 border-red-300 hover:bg-red-50"
                onClick={() => handleMenuChoice('invalid')}
                data-testid="sim-invalid"
              >
                <AlertCircle className="w-3 h-3" /> Invalid
              </Button>
            )}
          </div>
        </div>
      )}

      {isWaiting && sim.waitingFor === 'call_hours' && (
        <div className="border-t pt-3 shrink-0 space-y-2" data-testid="sim-hours-controls">
          <p className="text-[11px] font-medium text-muted-foreground uppercase tracking-wide">Is the business currently open?</p>
          <div className="flex gap-2">
            <Button size="sm" variant="outline" className="h-8 text-xs gap-1" onClick={() => handleHoursChoice('open')} data-testid="sim-hours-open">
              <Clock className="w-3 h-3" /> Open Hours
            </Button>
            <Button size="sm" variant="outline" className="h-8 text-xs gap-1" onClick={() => handleHoursChoice('closed')} data-testid="sim-hours-closed">
              <PhoneOff className="w-3 h-3" /> After Hours
            </Button>
          </div>
        </div>
      )}

      {isEnded && (
        <div className="border-t pt-3 shrink-0" data-testid="sim-ended">
          <div className="flex items-center gap-2 text-xs">
            <CheckCircle2 className="w-4 h-4 text-green-600 shrink-0" />
            <span className="text-muted-foreground">
              Simulation complete — {sim.log.length} events logged.
              {sim.terminal === 'voicemail' && ' Caller reached voicemail.'}
              {sim.terminal === 'end_call' && ' Call was terminated.'}
              {sim.terminal === 'forward' && ' Call was forwarded.'}
              {sim.terminal === 'ring' && ' Call ended after ringing.'}
            </span>
          </div>
        </div>
      )}
    </div>
  );
}


// ══════════════════════════════════════════════════════════════
// FlowSimulatorModal — overlay wrapper
// ══════════════════════════════════════════════════════════════

export function FlowSimulatorModal({ flow, onClose }) {
  if (!flow) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center" data-testid="sim-modal">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative bg-background rounded-xl shadow-2xl border w-full max-w-lg mx-4 mt-8 sm:mt-16 flex flex-col" style={{ maxHeight: '85vh' }}>
        {/* Modal header */}
        <div className="flex items-center justify-between px-4 sm:px-6 pt-4 sm:pt-6 pb-2 shrink-0">
          <div>
            <h3 className="text-sm font-semibold flex items-center gap-2">
              <Play className="w-4 h-4 text-blue-600" />
              Test Runner — {flow.name}
            </h3>
            <p className="text-[10px] text-muted-foreground mt-0.5">Non-destructive simulation. No real calls or billing.</p>
          </div>
          <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={onClose} data-testid="sim-close-btn">
            <X className="w-4 h-4" />
          </Button>
        </div>
        {/* Simulator body */}
        <div className="flex-1 overflow-hidden px-4 sm:px-6 pb-4 sm:pb-6">
          <FlowSimulator flow={flow} onClose={onClose} />
        </div>
      </div>
    </div>
  );
}
